import React from 'react';
import {
  RadarChart as RechartsRadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { COLORS } from '../utils/ChartConstants';
import { ChartData, NewChartData } from '../utils/ChartInterfaces';
import { ChartTypeDetector } from '../utils/ChartTypeDetector';

interface RadarChartProps {
  data: any[];
  originalChart?: ChartData;
}

// Custom tick component for wrapping long labels
const CustomTick: React.FC<any> = ({ payload, x, y, textAnchor, cx, cy }) => {
  const text = payload.value;
  const maxCharsPerLine = 15; // Max characters per line (more aggressive wrapping)
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  // Simple word wrapping
  words.forEach((word: string) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length > maxCharsPerLine) {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) lines.push(currentLine);

  // Calculate position offset based on angle
  const angle = Math.atan2(y - cy, x - cx);
  const offsetX = Math.cos(angle) * 20;
  const offsetY = Math.sin(angle) * 20;

  // Adjust vertical position for multi-line labels
  const verticalOffset = lines.length > 1 ? -(lines.length - 1) * 6 : 0;

  return (
    <text
      x={x + offsetX}
      y={y + offsetY + verticalOffset}
      textAnchor={textAnchor}
      fontSize="10px"
      fill="#666"
    >
      {lines.map((line, i) => (
        <tspan key={i} x={x + offsetX} dy={i === 0 ? 0 : 11}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

export const RadarChart: React.FC<RadarChartProps> = ({ data, originalChart }) => {
  // For radar charts, we need to handle the data differently
  // If we have the original chart structure with datasets, use that
  let radarData = data;
  let dataKeys: string[] = [];

  if (originalChart) {
    const isNew = ChartTypeDetector.isNewStructure(originalChart);
    const isLegacy = ChartTypeDetector.isLegacyStructure(originalChart);

    if (isNew) {
      const newChart = originalChart as NewChartData;
      // Normalize categories/series to labels/datasets
      const labels = newChart.labels || newChart.categories;
      const datasets = newChart.datasets || (newChart.series?.map(s => ({
        label: s.name,
        data: s.data
      })) || []);

      // Transform to radar format
      radarData = labels?.map((label, index) => {
        const point: any = { name: label };
        datasets.forEach(ds => {
          point[ds.label] = ds.data[index];
        });
        return point;
      }) || [];

      dataKeys = datasets.map(ds => ds.label);
    } else if (isLegacy) {
      const legacyChart = originalChart as any;
      // Handle legacy charts with categories/series or labels/datasets
      const labels = legacyChart.labels || legacyChart.categories;
      const datasets = legacyChart.datasets || (legacyChart.series?.map((s: any) => ({
        label: s.name,
        data: s.data
      })) || []);

      if (labels && datasets && datasets.length > 0) {
        radarData = labels.map((label: string, index: number) => {
          const point: any = { name: label };
          datasets.forEach((ds: any) => {
            point[ds.label] = ds.data[index];
          });
          return point;
        });

        dataKeys = datasets.map((ds: any) => ds.label);
      }
    }
  }

  // If we couldn't build radarData from original chart, try to extract from data
  if ((!dataKeys || dataKeys.length === 0) && data.length > 0) {
    radarData = data;
    dataKeys = Object.keys(data[0]).filter(key => key !== 'name' && typeof data[0][key] === 'number');
  }

  // If we still don't have proper data keys, use 'value' as default
  if (dataKeys.length === 0) {
    dataKeys = ['value'];
  }

  // Calculate max value from data for domain
  let maxValue = 0;
  radarData.forEach(point => {
    dataKeys.forEach(key => {
      const value = point[key];
      if (typeof value === 'number' && value > maxValue) {
        maxValue = value;
      }
    });
  });

  // Round up to nearest 10 for cleaner axis
  const domainMax = Math.ceil(maxValue / 10) * 10 || 10;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsRadarChart data={radarData} margin={{ top: 60, right: 100, bottom: 0, left: 100 }}>
        <PolarGrid />
        <PolarAngleAxis dataKey="name" tick={<CustomTick />} />
        <PolarRadiusAxis angle={90} domain={[0, domainMax]} />
        {dataKeys.map((key, i) => {
          const strokeColor = COLORS[i % COLORS.length];
          const fillColor = COLORS[i % COLORS.length];
          return (
            <Radar
              key={key}
              name={key}
              dataKey={key}
              stroke={strokeColor}
              fill={fillColor}
              fillOpacity={0.3}
            />
          );
        })}
        <Tooltip />
        {dataKeys.length > 1 && <Legend wrapperStyle={{ paddingTop: '0' }} />}
      </RechartsRadarChart>
    </ResponsiveContainer>
  );
};